import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Building2,
  Search,
  Users,
  GraduationCap,
  UserCheck,
  Users2,
  Loader2,
  Download,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  KeyRound,
  MoreVertical,
  Mail,
  Phone,
} from 'lucide-react';
import {
  getInstitutionUserStats,
  getInstitutionStaff,
  getInstitutionProfessors,
  getInstitutionStudents,
  getInstitutionParents,
  type InstitutionUserStats,
  type StaffUser,
  type ProfessorUser,
  type StudentUser,
  type ParentUser,
  type PaginationMeta,
} from '@/services/superAdminUserService';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import { UserExportModal } from '../components/UserExportModal';
import { ViewStudentModal } from '../components/ViewStudentModal';
import { ViewStaffModalSuperAdmin } from '../components/ViewStaffModalSuperAdmin';
import { DirectEditStaffProfileModalSuperAdmin } from '../components/DirectEditStaffProfileModalSuperAdmin';

type UserTab = 'staff' | 'professors' | 'students' | 'parents';

const ROLE_LABELS: Record<string, string> = {
  DIRECTOR_GENERAL: 'Directeur General',
  BRANCH_MANAGER: 'Directeur de Campus',
  PEDAGOGICAL_DIRECTOR: 'Directeur Pedagogique',
  FINANCIAL_MANAGER: 'Directeur Financier',
  MARKETING_MANAGER: 'Directeur Marketing',
  COMMERCIAL_MANAGER: 'Directeur Commercial',
  CALL_CENTER_AGENT: 'Agent Call Center',
  PROGRAM_HEAD: 'Chef de Filiere',
  ACADEMIC_COORDINATOR: 'Coordinateur Academique',
  TEACHER: 'Professeur',
  STUDENT: 'Etudiant',
  PARENT: 'Parent',
};

export const InstitutionUsersPage: React.FC = () => {
  const { institutionId } = useParams<{ institutionId: string }>();
  const navigate = useNavigate();
  const { toasts, removeToast, error: showError } = useToast();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<InstitutionUserStats | null>(null);
  const [activeTab, setActiveTab] = useState<UserTab>('staff');
  const [searchTerm, setSearchTerm] = useState('');
  const [academicYearId, setAcademicYearId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [institutionName, setInstitutionName] = useState<string>('');

  // Data state
  const [staffData, setStaffData] = useState<{ data: StaffUser[]; meta: PaginationMeta } | null>(null);
  const [professorsData, setProfessorsData] = useState<{ data: ProfessorUser[]; meta: PaginationMeta } | null>(null);
  const [studentsData, setStudentsData] = useState<{ data: StudentUser[]; meta: PaginationMeta } | null>(null);
  const [parentsData, setParentsData] = useState<{ data: ParentUser[]; meta: PaginationMeta } | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [viewingStudent, setViewingStudent] = useState<StudentUser | null>(null);

  // Staff/Professor action state
  const [openActionsDropdown, setOpenActionsDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, showAbove: false });
  const [viewingStaff, setViewingStaff] = useState<StaffUser | ProfessorUser | null>(null);
  const [editingStaff, setEditingStaff] = useState<StaffUser | ProfessorUser | null>(null);
  const [resetPasswordStaff, setResetPasswordStaff] = useState<StaffUser | ProfessorUser | null>(null);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Refs for dropdown positioning
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Ref to track if we've initialized the academic year (prevents race condition)
  const hasInitializedYearRef = useRef(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openActionsDropdown && actionsDropdownRef.current && !actionsDropdownRef.current.contains(e.target as Node)) {
        const button = dropdownButtonRefs.current.get(openActionsDropdown);
        if (!button?.contains(e.target as Node)) {
          setOpenActionsDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionsDropdown]);

  // Handle opening dropdown with positioning
  const handleOpenDropdown = (userId: string, buttonEl: HTMLButtonElement) => {
    if (openActionsDropdown === userId) {
      setOpenActionsDropdown(null);
      return;
    }
    const rect = buttonEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < 200;
    setDropdownPosition({
      top: showAbove ? rect.top : rect.bottom + 4,
      left: rect.right - 200,
      showAbove,
    });
    setOpenActionsDropdown(userId);
  };

  // Handle reset password
  const handleResetPassword = async (useEmail: boolean) => {
    if (!resetPasswordStaff || !institutionId) return;
    setIsResettingPassword(true);
    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/users/staff/${resetPasswordStaff.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': institutionId,
        },
        body: JSON.stringify({ sendEmail: useEmail }),
      });
      const data = await response.json();
      if (response.ok) {
        if (useEmail) {
          setResetPasswordSuccess('Un email de reinitialisation a ete envoye.');
        } else {
          setResetPasswordSuccess(`Mot de passe temporaire: ${data.tempPassword}`);
        }
      } else {
        showError('Erreur', data.message || 'Echec de la reinitialisation');
        setResetPasswordStaff(null);
      }
    } catch (error) {
      showError('Erreur', 'Echec de la reinitialisation du mot de passe');
      setResetPasswordStaff(null);
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Fetch stats - no longer depends on academicYearId to prevent race condition
  const fetchStats = useCallback(async () => {
    if (!institutionId) return;
    try {
      const data = await getInstitutionUserStats(institutionId, academicYearId || undefined);
      setStats(data);
      // Note: Default academic year is set in a separate effect below
    } catch (error) {
      console.error('Error fetching stats:', error);
      showError('Erreur', 'Impossible de charger les statistiques');
    }
  }, [institutionId]); // academicYearId REMOVED to prevent cascading re-renders

  // Set default academic year ONCE after stats are loaded
  useEffect(() => {
    if (stats && !hasInitializedYearRef.current && !academicYearId && stats.academicYears.length > 0) {
      const current = stats.academicYears.find(y => y.isCurrent);
      if (current) {
        hasInitializedYearRef.current = true;
        setAcademicYearId(current.id);
      }
    }
  }, [stats, academicYearId]);

  // Refetch stats when user manually changes academic year (after initialization)
  useEffect(() => {
    if (hasInitializedYearRef.current && academicYearId) {
      fetchStats();
    }
  }, [academicYearId, fetchStats]);

  // Fetch users based on active tab
  const fetchUsers = useCallback(async () => {
    if (!institutionId) return;
    setIsLoadingData(true);
    try {
      const filters = {
        academicYearId: academicYearId || undefined,
        search: searchTerm || undefined,
        page,
        limit: 20,
      };

      switch (activeTab) {
        case 'staff':
          const staff = await getInstitutionStaff(institutionId, filters);
          setStaffData(staff);
          break;
        case 'professors':
          const profs = await getInstitutionProfessors(institutionId, filters);
          setProfessorsData(profs);
          break;
        case 'students':
          const students = await getInstitutionStudents(institutionId, filters);
          setStudentsData(students);
          break;
        case 'parents':
          const parents = await getInstitutionParents(institutionId, filters);
          setParentsData(parents);
          break;
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showError('Erreur', 'Impossible de charger les utilisateurs');
    } finally {
      setIsLoadingData(false);
    }
  }, [institutionId, activeTab, academicYearId, searchTerm, page]);

  // Fetch institution name
  useEffect(() => {
    const fetchInstitutionName = async () => {
      if (!institutionId) return;
      try {
        const token = localStorage.getItem('nexedu_access_token');
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institutionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setInstitutionName(data.name);
        }
      } catch (error) {
        console.error('Error fetching institution:', error);
      }
    };
    fetchInstitutionName();
  }, [institutionId]);

  // Initial load
  useEffect(() => {
    setIsLoading(true);
    fetchStats().finally(() => setIsLoading(false));
  }, [fetchStats]);

  // Fetch users when tab/filters change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset page when search or tab changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, activeTab, academicYearId]);

  const getCurrentData = () => {
    switch (activeTab) {
      case 'staff':
        return staffData;
      case 'professors':
        return professorsData;
      case 'students':
        return studentsData;
      case 'parents':
        return parentsData;
      default:
        return null;
    }
  };

  const currentData = getCurrentData();

  const tabs = [
    { id: 'staff' as UserTab, label: 'Personnel', icon: UserCheck, count: stats?.staffCount || 0, color: 'blue' },
    { id: 'professors' as UserTab, label: 'Professeurs', icon: GraduationCap, count: stats?.professorCount || 0, color: 'purple' },
    { id: 'students' as UserTab, label: 'Etudiants', icon: Users, count: stats?.studentCount || 0, color: 'green' },
    { id: 'parents' as UserTab, label: 'Parents', icon: Users2, count: stats?.parentCount || 0, color: 'amber' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/admin/etablissements/utilisateurs')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retour aux etablissements</span>
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{institutionName || 'Chargement...'}</h1>
                <p className="text-sm text-gray-500">Gestion des utilisateurs</p>
              </div>
            </div>

            {/* Academic Year Filter */}
            <div className="flex items-center gap-3">
              <select
                value={academicYearId}
                onChange={(e) => setAcademicYearId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Toutes les annees</option>
                {stats?.academicYears.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name} {year.isCurrent ? '(Actuelle)' : ''}
                  </option>
                ))}
              </select>

              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                Exporter
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const bgColors: Record<string, string> = {
              blue: 'bg-blue-100',
              purple: 'bg-purple-100',
              green: 'bg-green-100',
              amber: 'bg-amber-100',
            };
            const iconColors: Record<string, string> = {
              blue: 'text-blue-600',
              purple: 'text-purple-600',
              green: 'text-green-600',
              amber: 'text-amber-600',
            };
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`bg-white rounded-xl border-2 p-4 flex items-center gap-4 transition-all ${
                  activeTab === tab.id
                    ? 'border-indigo-500 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`p-3 rounded-lg ${bgColors[tab.color]}`}>
                  <Icon className={`h-6 w-6 ${iconColors[tab.color]}`} />
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-500">{tab.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{tab.count.toLocaleString()}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Search and Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, email ou code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoadingData ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
              </div>
            ) : !currentData || currentData.data.length === 0 ? (
              <div className="text-center py-20">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun utilisateur trouve</p>
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Nom Complet</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Telephone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Email</th>
                      {activeTab === 'staff' && (
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Role</th>
                      )}
                      {activeTab === 'professors' && (
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Specialite</th>
                      )}
                      {activeTab === 'students' && (
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Programme</th>
                      )}
                      {activeTab === 'parents' && (
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Etudiants lies</th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Campus</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activeTab === 'staff' && staffData?.data.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono">{user.staffCode || '-'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" />
                              {user.phone}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.email ? (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {user.email}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{ROLE_LABELS[user.role] || user.role}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.campuses?.map(c => c.sigle || c.name).join(', ') || '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            ref={(el) => { if (el) dropdownButtonRefs.current.set(user.id, el); }}
                            onClick={(e) => handleOpenDropdown(user.id, e.currentTarget)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openActionsDropdown === user.id && createPortal(
                            <div
                              ref={actionsDropdownRef}
                              className="fixed w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999]"
                              style={{
                                top: dropdownPosition.showAbove ? 'auto' : dropdownPosition.top,
                                bottom: dropdownPosition.showAbove ? window.innerHeight - dropdownPosition.top + 4 : 'auto',
                                left: Math.max(8, dropdownPosition.left),
                              }}
                            >
                              <button
                                onClick={() => { setViewingStaff(user); setOpenActionsDropdown(null); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <Eye size={16} className="text-gray-400" />
                                Voir les details
                              </button>
                              <button
                                onClick={() => { setEditingStaff(user); setOpenActionsDropdown(null); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <Edit size={16} className="text-gray-400" />
                                Modifier le profil
                              </button>
                              <button
                                onClick={() => { setResetPasswordStaff(user); setResetPasswordSuccess(null); setOpenActionsDropdown(null); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <KeyRound size={16} className="text-blue-500" />
                                Reinitialiser mot de passe
                              </button>
                            </div>,
                            document.body
                          )}
                        </td>
                      </tr>
                    ))}

                    {activeTab === 'professors' && professorsData?.data.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono">{user.staffCode || '-'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" />
                              {user.phone}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.email ? (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {user.email}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{user.specialite || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.campuses?.map(c => c.sigle || c.name).join(', ') || '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            ref={(el) => { if (el) dropdownButtonRefs.current.set(user.id, el); }}
                            onClick={(e) => handleOpenDropdown(user.id, e.currentTarget)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openActionsDropdown === user.id && createPortal(
                            <div
                              ref={actionsDropdownRef}
                              className="fixed w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999]"
                              style={{
                                top: dropdownPosition.showAbove ? 'auto' : dropdownPosition.top,
                                bottom: dropdownPosition.showAbove ? window.innerHeight - dropdownPosition.top + 4 : 'auto',
                                left: Math.max(8, dropdownPosition.left),
                              }}
                            >
                              <button
                                onClick={() => { setViewingStaff(user); setOpenActionsDropdown(null); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <Eye size={16} className="text-gray-400" />
                                Voir les details
                              </button>
                              <button
                                onClick={() => { setEditingStaff(user); setOpenActionsDropdown(null); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <Edit size={16} className="text-gray-400" />
                                Modifier le profil
                              </button>
                              <button
                                onClick={() => { setResetPasswordStaff(user); setResetPasswordSuccess(null); setOpenActionsDropdown(null); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <KeyRound size={16} className="text-blue-500" />
                                Reinitialiser mot de passe
                              </button>
                            </div>,
                            document.body
                          )}
                        </td>
                      </tr>
                    ))}

                    {activeTab === 'students' && studentsData?.data.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono">{student.studentCode}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.firstName} {student.lastName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {student.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" />
                              {student.phone}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {student.email ? (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {student.email}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{student.program?.name || student.diplomeInscrit || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{student.campus?.sigle || student.campus?.name || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setViewingStudent(student)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {activeTab === 'parents' && parentsData?.data.map((parent) => (
                      <tr key={parent.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono">{parent.id.slice(0, 8)}...</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{parent.firstName} {parent.lastName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {parent.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" />
                              {parent.phone}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {parent.email ? (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {parent.email}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {parent.linkedStudents?.length > 0 ? (
                            <span className="flex flex-wrap gap-1">
                              {parent.linkedStudents.map((s) => (
                                <span key={s.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                                  {s.firstName} {s.lastName}
                                </span>
                              ))}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {parent.linkedStudents?.[0]?.campus?.sigle || '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {currentData && currentData.meta.totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      Page {currentData.meta.page} sur {currentData.meta.totalPages} ({currentData.meta.total} resultats)
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={!currentData.meta.hasPreviousPage}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={!currentData.meta.hasNextPage}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && institutionId && (
        <UserExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          userType={activeTab}
          institutionId={institutionId}
          institutionName={institutionName}
          academicYearId={academicYearId}
          currentData={currentData?.data || []}
        />
      )}

      {/* View Student Modal */}
      {viewingStudent && (
        <ViewStudentModal
          isOpen={!!viewingStudent}
          onClose={() => setViewingStudent(null)}
          student={viewingStudent}
        />
      )}

      {/* View Staff Modal */}
      {viewingStaff && institutionId && (
        <ViewStaffModalSuperAdmin
          isOpen={!!viewingStaff}
          onClose={() => setViewingStaff(null)}
          staff={viewingStaff}
          tenantId={institutionId}
        />
      )}

      {/* Edit Staff Modal */}
      {editingStaff && institutionId && (
        <DirectEditStaffProfileModalSuperAdmin
          isOpen={!!editingStaff}
          onClose={() => setEditingStaff(null)}
          staff={editingStaff}
          tenantId={institutionId}
          onSuccess={() => {
            fetchUsers();
          }}
        />
      )}

      {/* Reset Password Modal */}
      {resetPasswordStaff && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setResetPasswordStaff(null); setResetPasswordSuccess(null); }} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Reinitialiser le mot de passe</h3>
              <p className="text-sm text-gray-500 mt-1">
                {resetPasswordStaff.firstName} {resetPasswordStaff.lastName}
              </p>
            </div>
            <div className="p-6">
              {resetPasswordSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium">{resetPasswordSuccess}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Choisissez une methode pour reinitialiser le mot de passe:
                  </p>
                  <button
                    onClick={() => handleResetPassword(true)}
                    disabled={isResettingPassword}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Mail size={18} />
                    Envoyer un email de reinitialisation
                  </button>
                  <button
                    onClick={() => handleResetPassword(false)}
                    disabled={isResettingPassword}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <KeyRound size={18} />
                    Generer un mot de passe temporaire
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => { setResetPasswordStaff(null); setResetPasswordSuccess(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {resetPasswordSuccess ? 'Fermer' : 'Annuler'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InstitutionUsersPage;
